import React, { useMemo, useState } from "react";
import { useAppDispatch } from "../../store";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import HexagonGraph from "../graphs/HexagonGraph";
import StackedAreaGraphContainer from "../graphs/StackedAreaGraph/StackedAreaGraphContainer";
import BarChartContainer from "../graphs/BarChart/BarChartContainer";
import Select from "../ui/select/Select";
import { CircleLoader } from "../../icons/Loaders";
import { fetchActiveFeedItem } from "../../store/slices/activeFeedItem";
import "./StoryReach.scss";
import "./SocialScore.scss";

type SocialScoreProps = {
  engagementData: Array<any>,
  loading: boolean,
  type?: string,
  secondaryPosts: Array<any>,
};

const SocialScore = ({ engagementData, loading, type = "", secondaryPosts }: SocialScoreProps) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const [selectedPost, setSelectedPost] = useState(-1);

  const PreparedHexagonChartData = useMemo(() => {
    if (!engagementData) return {};
    return {
      expectation: engagementData.expectation,
      factor: engagementData.engagement_factor,
      index: engagementData.engagement_story_expectation_index,
      story_expectation: engagementData.engagement_story_expectation,
      story_expectation_index: engagementData.engagement_story_expectation_index,
      value: engagementData.performance,
    };
  }, [engagementData]);

  const PreparedAreaGraphData = useMemo(() => {
    if (!engagementData) return [];
    const channels = engagementData?.channels?.filter((e) => e.performance > 0);
    let channelsWithData = [];

    const acumulateData = (metrics) => {
      const meticsForTimestamps = metrics[0].graphdata[0];
      let sumData = [];

      for (let i = 0; i < meticsForTimestamps.length; i++) {
        const timestamp = meticsForTimestamps[i][0];

        let sum = 0;
        metrics.forEach((metric) => {
          sum += metric.graphdata[0][i][1];
        });
        sumData.push([timestamp, sum]);
      }
      return sumData;
    };

    channels.forEach((channel) => {
      channelsWithData.push({
        color: channel.color,
        data: acumulateData(channel.metrics),
        id: channel.channel,
        name: channel.name,
        total: channel.performance,
      });
    });

    return channelsWithData;
  }, [engagementData]);

  const PreparedBarChartData = useMemo(() => {
    if (!engagementData) return [];
    const channels = engagementData?.channels?.filter((e) => e.performance > 0);
    const channelsWithData = channels.map((e) => {
      return {
        name: e.channel,
        performance: e.performance,
        data: e.metrics.map((metric) => {
          return {
            value: metric.count,
            color: metric.color,
            title: metric.name,
          };
        }),
      };
    });

    return channelsWithData;
  }, [engagementData]);

  const postsList = useMemo(() => {
    if (!secondaryPosts) return [];

    let posts = secondaryPosts
      .filter((post) => post.post_type === "fb_post")
      .map((post) => {
        return { label: post.content, key: post.post_type_id, value: post.post_type_id };
      });
    posts.unshift({ label: "No post selected", key: -1, value: -1 });

    return posts;
  }, [secondaryPosts]);



  const currentSelectedPost = useMemo(() => {
    if (selectedPost === -1) return "No post selected";
    else if (postsList.length === 0) return "No post selected";
    else {
      const foundPost = postsList.filter((post) => {
        return post.key === selectedPost;
      })[0]?.label;
      return foundPost? foundPost : "No post selected";
    }
  }, [postsList, selectedPost]);

  const handlePostChange = (post) => {
    setSelectedPost(post);
    if (post !== -1) {
      dispatch(
        fetchActiveFeedItem(id, {
          facebookPostId: post,
        }),
      );
    } else {
      dispatch(fetchActiveFeedItem(id));
    }
  };

  return (
    <div className="story-reach-container">
      <h3>{t("story.socialScore.title")}</h3>
      <div className="graphs-container">
        <div className="page-views-graph">
          <h4>{t("story.socialScore.socialInteractions")}</h4>

          {loading ? (
            <div className="loader-container">
              <CircleLoader />
            </div>
          ) : (
            <>
              <p className="performance">
                {t("story.common.performance")} x {engagementData?.engagement_factor}
              </p>
              <div className="circle-graph-container">
                <HexagonGraph data={PreparedHexagonChartData} key={PreparedHexagonChartData.value} />
                <h2 className="performance-index">{engagementData?.engagement_calc_result}</h2>
              </div>
              {type !== "videos" && (
                <div className="pageviews-expectations">
                  <p className="expectations-label">{t("story.common.storyExpectations")}</p>
                  {engagementData?.engagement_story_expectation_index ? (
                    <p className="expectations-value">{engagementData.engagement_story_expectation_index}</p>
                  ) : (
                    <p className="expectations-not-available">{t("story.common.notAvailable")}</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className="origins-graph">
          <h4>{t("story.socialScore.engagementFlow")}</h4>
          <div>
            {loading ? (
              <div className="loader-container">
                <CircleLoader />
              </div>
            ) : (
              <>
                {type !== "videos" && (
                  <div className="segment-select-container">
                    <Select
                      options={postsList}
                      setSelectedOption={(post) => {
                        handlePostChange(post);
                      }}
                      selectedOption={currentSelectedPost.substring(0, 25)}
                      styling={{
                        selectOptionsContainerClassName: "segment-dropdown-container",
                      }}
                      fullWidth
                    />
                  </div>
                )}

                <StackedAreaGraphContainer data={PreparedAreaGraphData} />
                {type === "videos" && (
                  <p className="video-graph-explanation">
                    The graph is calculated by smartocto, based on gathered data from various sources. In the numbers
                    selectedSections of this page you will find the precise data from multiple API's.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
        <div className="details-graph">
          <h4>{t("story.common.details")}</h4>
          {loading ? (
            <div className="loader-container">
              <CircleLoader />
            </div>
          ) : (
            <div className="social-channels-bargraphs-container">
              {(engagementData?.performance || PreparedBarChartData.length > 0) &&
                PreparedBarChartData.map((channel) => {
                  return (
                    <div key={channel.name}>
                      <p className="social-channel-title">{channel.name}</p>
                      <p className="social-channel-performance">{channel.performance}</p>
                      <BarChartContainer data={channel.data} />
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocialScore;
